if (!customElements.get('variant-picker')) {
  class VariantPicker extends window.BaseComponent {
    constructor() {
      super();
    }

    init() {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.sectionId = this.dataset.section;
      this.updateOptions();
      this.injectSoldOutStyles();
      this.markSoldOut();
      this.addEventListener('change', this.onOptionChange.bind(this));

      // Handle deep linking on load — defer so Swiper/Lenis are fully ready
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');
      if (variantId) {
        setTimeout(() => this.selectVariantById(variantId), 500);
      } else {
        // Sync initial state from pre-checked radios if no deep link
        this.onOptionChange();
      }
    }

    onOptionChange() {
      // Freeze Lenis to prevent scroll position corruption during DOM changes
      const mainContent = document.getElementById('MainContent');
      const scrollBefore = mainContent ? mainContent.scrollTop : 0;

      if (window.lenis) window.lenis.stop();

      this.updateOptions();
      this.markSoldOut();
      this.currentVariant = this.variants.find((variant) => {
        return !variant.options.map((option, index) => {
          return this.options[index] === option;
        }).includes(false);
      });

      if (this.currentVariant) {
        this.updateURL();
        this.updateVariantInput();
        this.renderProductInfo();

        if (window.pubsub) {
          window.pubsub.publish('variant-change', this.currentVariant);
        }

        // Wait for Swiper autoHeight transition (300ms CSS) to complete,
        // then recalculate Lenis dimensions and resume scrolling
        setTimeout(() => {
          // Restore native scrollTop in case Lenis or Swiper moved it
          if (mainContent) mainContent.scrollTop = scrollBefore;

          if (window.lenis) {
            window.lenis.resize();
            window.lenis.start();
          }
        }, 400);
      } else {
        // No variant found — just resume Lenis
        if (window.lenis) window.lenis.start();
      }
    }

    updateOptions() {
      this.options = Array.from(this.querySelectorAll('input:checked'), (input) => input.value);
    }

    /* Thin X over sizes that are sold out. Checks each choice against the
       other options currently selected, so it also works for products with
       more than one option (e.g. color + size). Sold out sizes stay tappable,
       and the button still reads SOLD OUT when one is picked. */
    markSoldOut() {
      const groups = this.querySelectorAll('.product__option');
      groups.forEach((group, index) => {
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const wanted = this.options.slice();
          wanted[index] = input.value;
          const match = this.variants.find((variant) =>
            variant.options.every((value, i) => wanted[i] === undefined || wanted[i] === value)
          );
          const soldOut = !match || !match.available;
          const label = this.querySelector(`label[for="${input.id}"]`);
          if (label) {
            label.classList.toggle('is-sold-out', soldOut);
            if (soldOut) label.setAttribute('title', 'Sold out');
            else label.removeAttribute('title');
          }
          input.setAttribute('aria-label', soldOut ? `${input.value}, sold out` : input.value);
        });
      });
    }

    injectSoldOutStyles() {
      if (document.getElementById('SoldOutPillStyles')) return;
      const style = document.createElement('style');
      style.id = 'SoldOutPillStyles';
      style.textContent = [
        '.product__option-pill.is-sold-out { position: relative; color: rgba(18, 18, 18, 0.45); }',
        '.product__option-pill.is-sold-out::after {',
        '  content: ""; position: absolute; inset: 0; pointer-events: none; border-radius: inherit;',
        '  background:',
        '    linear-gradient(to top right, transparent calc(50% - 0.5px), rgba(18, 18, 18, 0.55) calc(50% - 0.5px), rgba(18, 18, 18, 0.55) calc(50% + 0.5px), transparent calc(50% + 0.5px)),',
        '    linear-gradient(to top left, transparent calc(50% - 0.5px), rgba(18, 18, 18, 0.55) calc(50% - 0.5px), rgba(18, 18, 18, 0.55) calc(50% + 0.5px), transparent calc(50% + 0.5px));',
        '}',
      ].join('\n');
      document.head.appendChild(style);
    }

    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
      const section = document.getElementById(`ProductSection-${this.sectionId}`) || document;
      const productForms = section.querySelectorAll('form[data-type="add-to-cart-form"], #product-form');
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (!input) return;
        input.value = this.currentVariant.id;
        input.removeAttribute('disabled');
      });
    }

    renderProductInfo() {
      // Scope all queries to the product section to avoid hitting
      // dynamic checkout button elements or other unrelated DOM
      const section = document.getElementById(`ProductSection-${this.sectionId}`) || document;

      // Update Price — only target our own .product__price elements
      const priceContainers = section.querySelectorAll('.product__price[data-price]');
      priceContainers.forEach(container => {
        container.textContent = this.formatMoney(this.currentVariant.price);
      });

      // Update ATC Button — only target our own submit button with [data-atc-text]
      const atcButtons = section.querySelectorAll('button[name="add"]');
      atcButtons.forEach(button => {
        const textSpan = button.querySelector('[data-atc-text]');
        if (!textSpan) return;

        if (this.currentVariant.available) {
          button.removeAttribute('disabled');
          const atcLabel = this.dataset.preorder === 'true'
            ? (this.dataset.preorderLabel || 'PRE-ORDER')
            : (this.dataset.addToCartLabel || 'ADD TO CART');
          textSpan.textContent = atcLabel;
        } else {
          button.setAttribute('disabled', 'disabled');
          textSpan.textContent = 'SOLD OUT';
        }
      });
    }

    selectVariantById(id) {
      const variant = this.variants.find(v => v.id == id);
      if (variant) {
        variant.options.forEach((option, index) => {
          const radio = this.querySelector(`input[value="${option}"]`);
          if (radio) radio.checked = true;
        });
        this.onOptionChange();
      }
    }

    formatMoney(cents) {
      // Simple formatter, in a real theme use Shopify.formatMoney if available
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  if (window.BaseComponent) {
    customElements.define('variant-picker', VariantPicker);
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      if (window.BaseComponent) customElements.define('variant-picker', VariantPicker);
    });
  }
}
